import axios from 'axios';
import { environment } from '../environment';
import { profileService } from './profileService';

const ASTROLOGY_API_URL = environment.REACT_APP_API_URL;

export interface HoroscopeRequest {
  zodiac_sign?: string;
  date?: string;
  city?: string;
  birth_date?: string;
  birth_time?: string;
  birth_city?: string;
}

export interface CompatibilityRequest {
  your_sign: string;
  partner_sign: string;
  relationship_type: string;
}

export interface ChatRequest {
  question: string;
  context?: string;
}

export interface PalmAnalysisResponse {
  success?: boolean;
  status?: string;
  analysis?: string;
  image_url?: string;
  error?: string;
  detail?: string;
  sections?: Record<string, string>;
  confidence?: number;
  predictions?: Array<{
    class: string;
    confidence: number;
  }>;
}

export interface HoroscopeResponse {
  success: boolean;
  horoscope?: string;
  error?: string;
}

export interface CompatibilityResponse {
  success: boolean;
  compatibility?: string;
  percentage?: number;
  analysis?: string;
  error?: string;
}

export interface ChatResponse {
  success: boolean;
  response?: string;
  error?: string;
}

export const astrologyService = {
  getHoroscope: async (data: HoroscopeRequest): Promise<HoroscopeResponse> => {
    const response = await axios.post(`${ASTROLOGY_API_URL}/api/simple-horoscope`, data);
    return response.data;
  },

  getAdvancedHoroscope: async (data: HoroscopeRequest): Promise<HoroscopeResponse> => {
    const response = await axios.post(`${ASTROLOGY_API_URL}/api/horoscope`, data);
    return response.data;
  },

  generateKundli: async (data: {
    birth_date: string;
    birth_time: string;
    birth_city: string;
  }): Promise<any> => {
    const response = await axios.post(`${ASTROLOGY_API_URL}/generate-horoscope`, {
      birth_date: data.birth_date,
      birth_time: data.birth_time,
      birth_city: data.birth_city
    });
    return response.data;
  },

  analyzePalm: async (image: File): Promise<PalmAnalysisResponse> => {
    const formData = new FormData();
    formData.append('file', image);
    
    try {
      const response = await axios.post(`${ASTROLOGY_API_URL}/api/palm-analysis`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Palm analysis API error:', error);
      throw error;
    }
  },

  checkCompatibility: async (data: CompatibilityRequest): Promise<CompatibilityResponse> => {
    const response = await axios.post(`${ASTROLOGY_API_URL}/api/compatibility`, data);
    return response.data;
  },

  chat: async (data: ChatRequest, onChunk?: (chunk: string) => void): Promise<ChatResponse> => {
    const { question, context } = data;
    let payload: any = { question };
    if (context) {
      payload.context = context;
    }
    
    const profile = await profileService.getProfile();
    if (profile) {
      payload.dob = profile.dob;
      payload.time_of_birth = profile.time_of_birth;
      payload.place_of_birth = profile.place_of_birth;
      payload.zodiac_sign = profile.zodiac_sign;
    }

    if (onChunk) {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use fetch for streaming
      let response = await fetch(`${ASTROLOGY_API_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const refreshRes = await fetch('https://keycloak-auth.babydino.co.in/auth/refresh', {
              method: 'POST',
              credentials: 'include'
            });
            if (refreshRes.ok) {
              const { token: newToken } = await refreshRes.json();
              localStorage.setItem('token', newToken);
              headers['Authorization'] = `Bearer ${newToken}`;
              
              // Retry original request
              response = await fetch(`${ASTROLOGY_API_URL}/api/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
              });
            }
          } catch (e) {
            console.warn('Token refresh failed during chat fetch');
          }

          if (!response.ok && response.status === 401) {
            console.warn("Unauthorized access - clearing token and redirecting to login");
            localStorage.removeItem('token');
            if (window.location.pathname !== '/login' && window.location.pathname !== '/welcome') {
              localStorage.setItem('intendedDestination', window.location.href);
              window.location.href = '/login';
            }
          }
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      if (!response.body) {
        throw new Error('ReadableStream not yet supported in this browser.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullResponse += chunk;
        onChunk(chunk);
      }
      return { success: true, response: fullResponse };
    } else {
      // Fallback to non-streaming if no callback provided
      const response = await axios.post(`${ASTROLOGY_API_URL}/api/chat`, payload);
      return response.data;
    }
  },

  getCities: async (): Promise<{ status: string; cities: string[]; total: number }> => {
    const response = await axios.get(`${ASTROLOGY_API_URL}/cities`);
    return response.data;
  },

  getZodiacSigns: async (): Promise<{ status: string; signs: string[] }> => {
    const response = await axios.get(`${ASTROLOGY_API_URL}/zodiac-signs`);
    return response.data;
  }
};
